import React from 'react';
import styled from 'styled-components';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { AlertOutlineSvg } from 'src/assets/dynamicSvg';
import { ColorSet } from 'src/redux/reducers/theme';

type ResultVariant = 'warning' | 'invalid';

const ACCENT: Record<ResultVariant, keyof ColorSet> = {
  warning: 'statusAmber',
  invalid: 'statusRed',
};

// No own border/radius — it sits at the top of a merged card that supplies the outer border.
const Banner = styled.div<{ $accent: keyof ColorSet }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: ${({ theme, $accent }) => `${theme.colors[$accent]}1f`};
`;

const DiscOuter = styled.div<{ $accent: keyof ColorSet }>`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $accent }) => `${theme.colors[$accent]}40`};
`;

const DiscInner = styled.div<{ $accent: keyof ColorSet }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $accent }) => theme.colors[$accent]};
`;

// Always white — the glyph sits on a saturated disc in both themes.
const CrossIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4L12 12M12 4L4 12" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// The AI's advisory "improved narrative" (validation.suggestion) — a white card on the tinted banner
// so it reads as a distinct, actionable rewrite the user can adopt to re-analyse.
const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const SuggestionCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  padding: 12px 16px;
`;

const UseLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.cflowerBlue};
  transition: color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
`;

interface ResultBannerProps {
  variant: ResultVariant;
  heading: string;
  body: string;
  /** AI's advisory improved-narrative (validation.suggestion); hidden when absent. */
  suggestion?: string;
  /** Adopt the suggestion into the input (so the user can re-analyse); the "Use this" link is hidden without it. */
  onUseSuggestion?: () => void;
}

export const ResultBanner: React.FC<ResultBannerProps> = ({ variant, heading, body, suggestion, onUseSuggestion }) => {
  const accent = ACCENT[variant];

  return (
    <Banner $accent={accent}>
      <DiscOuter $accent={accent}>
        <DiscInner $accent={accent}>
          {variant === 'warning' ? <AlertOutlineSvg color="#FFFFFF" style={{ width: 24, height: 24 }} /> : <CrossIcon />}
        </DiscInner>
      </DiscOuter>
      <Content>
        <Poppins px={16} weight={600} color="prussianBlue">
          {heading}
        </Poppins>
        <Spacer $px={4} />
        <Poppins px={14} color="cflowerBlue" css="line-height: 1.5;">
          {body}
        </Poppins>
        {suggestion && (
          <>
            <Spacer $px={12} />
            <SuggestionCard>
              <Poppins px={12} weight={600} color="cflowerBlue" css="text-transform: uppercase; letter-spacing: 0.04em;">
                Suggested description
              </Poppins>
              <Spacer $px={4} />
              <Poppins px={14} color="prussianBlue" css="line-height: 1.5;">
                {suggestion}
              </Poppins>
              {onUseSuggestion && (
                <>
                  <Spacer $px={8} />
                  <UseLink type="button" onClick={onUseSuggestion} data-cy="ai-use-suggestion">
                    Use this description
                  </UseLink>
                </>
              )}
            </SuggestionCard>
          </>
        )}
      </Content>
    </Banner>
  );
};
